import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, LogOut, MessageSquare, Lightbulb, UserCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { trackEvent } from '@/lib/gtm';
import { NotificationPopover } from '@/components/notifications/NotificationPopover';
import { SHOW_CREATORS_FEATURE } from '@/lib/constants';

interface Props {
  onOpenCreators: () => void;
  onOpenFeedback: () => void;
  onOpenCompleteProfile: () => void;
}

export const MobileProfileMenu = ({ onOpenCreators, onOpenFeedback, onOpenCompleteProfile }: Props) => {
    const { user, profile, signOut } = useAuth();
    const { planName, badgeStyles } = useSubscription();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Fechar ao clicar fora do componente
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="flex items-center gap-3" ref={menuRef}>
            {/* Bell Icon Popover */}
            <NotificationPopover 
                onOpenCompleteProfile={onOpenCompleteProfile} 
                align="right" 
                placement="bottom" 
                isMobile 
            />
            
            {/* Avatar Profile Dropdown */}
            <div className="relative">
                <button 
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-700 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500 active:scale-95 transition-all flex items-center justify-center p-0"
                >
                    {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="Usuário" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-800">
                            <UserCircle2 size={24} />
                        </div>
                    )}
                </button>

                {/* Dropdown Card */}
                {isOpen && (
                    <div className="absolute right-0 mt-3 w-60 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 animate-in slide-in-from-top-2 fade-in duration-200 overflow-hidden text-slate-200">
                        <div className="p-4 border-b border-slate-700 bg-slate-900/50 flex items-center gap-3">
                            <div className="w-10 h-10 shrink-0 rounded-full bg-slate-700 overflow-hidden flex items-center justify-center">
                                {profile?.avatar_url ? (
                                    <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <UserCircle2 size={24} className="text-slate-400" />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-white truncate text-sm">
                                    {profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'Usuário'}
                                </p>
                                <p className={`text-xs ${badgeStyles.textColor} font-medium truncate mt-0.5`}>{planName}</p>
                            </div>
                        </div>
                        
                        <div className="p-2 flex flex-col gap-1">
                            <Link 
                                to="/configuracoes" 
                                onClick={() => { trackEvent('menu_settings_clicked'); setIsOpen(false); }}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-700/80 hover:text-white transition-colors"
                            >
                                <Settings size={18} className="text-indigo-400" />
                                <span className="text-sm font-medium">Configurações</span>
                            </Link>
                            
                            <button 
                                onClick={() => { trackEvent('feedback_modal_opened'); setIsOpen(false); onOpenFeedback(); }}
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-700/80 hover:text-white transition-colors w-full text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <MessageSquare size={18} className="text-blue-400" />
                                    <span className="text-sm font-medium">Feedback</span>
                                </div>
                            </button>
                            
                            {SHOW_CREATORS_FEATURE && (
                                <button 
                                    onClick={() => { trackEvent('menu_creators_clicked'); setIsOpen(false); onOpenCreators(); }}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-700/80 hover:text-white transition-colors w-full text-left"
                                >
                                    <Lightbulb size={18} className="text-amber-400" />
                                    <span className="text-sm font-medium">Criadores</span>
                                </button>
                            )}
                        </div>

                        <div className="p-2 border-t border-slate-700 bg-slate-900/30">
                            <button 
                                onClick={() => { setIsOpen(false); signOut(); }}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-500/10 text-red-400 hover:text-red-300 transition-colors w-full text-left"
                            >
                                <LogOut size={18} />
                                <span className="text-sm font-medium">Sair</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
